import type { DraftEntry, DraftSet, LoggerDraft } from './loggerDraftTypes';
import type {
  ExerciseMeta,
  LoggedSetDraft,
  WorkoutState,
} from './loggerTimelineTypes';

// flattenDraft ↔ assembleDraft are the Phase-1 bridge between the nested
// LoggerDraft (what the card UI + buildPayload speak today) and the flat
// WorkoutState (the new source of truth). They are mutual inverses on the
// data: assembleDraft(flattenDraft(d)) deep-equals d. The flat store is
// FLATTEN-ONLY (see loggerTimelineTypes.ts) — no semantics change, so the
// round-trip is exact and the slice-0 oracle snapshots stay green.

// Nested → flat. Walks entries in order, lifting each entry's chrome into the
// `exercises` roster and each of its sets into the `timeline`, tagging every
// set with its entry's id + exerciseId. `genId` mints the per-set and
// per-entry ids; the caller owns id generation (a uuid factory in the live
// app, a deterministic counter in tests — the values are opaque, only their
// stability matters).
//
// id-PRESERVING: when an entry/set already carries an `entryId`/`id` (it came
// from assembleDraftKeepIds round-tripping through the reducer), that id is
// REUSED rather than re-minted, so a logged set keeps its identity across
// edits. A plain LoggerDraft (persisted, hydrated, or a test fixture) carries
// none, so every id is freshly minted — identical to the original behaviour.
export const flattenDraft = (
  draft: LoggerDraft,
  genId: () => string,
): WorkoutState => {
  const exercises: ExerciseMeta[] = [];
  const timeline: LoggedSetDraft[] = [];
  const seenIds = new Set<string>();
  for (const entry of draft.entries) {
    // Destructure entryId out explicitly (when present) so `meta` never
    // carries a stale copy; mint one when the entry is plain (fixture /
    // persisted / hydrated).
    const { sets, entryId: existingEntryId, ...meta } = entry as DraftEntry & {
      entryId?: string;
    };
    // A duplicate entryId would merge two entries' sets: mint a fresh one, keep both.
    const entryId =
      existingEntryId !== undefined && !seenIds.has(existingEntryId) ? existingEntryId : genId();
    seenIds.add(entryId);
    exercises.push({ ...meta, entryId });
    for (const set of sets) {
      timeline.push({
        ...set,
        id: (set as { id?: string }).id ?? genId(),
        entryId,
        exerciseId: entry.exerciseId,
      });
    }
  }
  return {
    ...(draft.sessionId !== undefined ? { sessionId: draft.sessionId } : {}),
    startedAt: draft.startedAt,
    ...(draft.endedAt !== undefined ? { endedAt: draft.endedAt } : {}),
    ...(draft.fromPlanId !== undefined ? { fromPlanId: draft.fromPlanId } : {}),
    ...(draft.coachNotes !== undefined ? { coachNotes: draft.coachNotes } : {}),
    ...(draft.coachNotesRef !== undefined
      ? { coachNotesRef: draft.coachNotesRef }
      : {}),
    ...(draft.todayProposal !== undefined
      ? { todayProposal: draft.todayProposal }
      : {}),
    ...(draft.roundType !== undefined ? { roundType: draft.roundType } : {}),
    ...(draft.name !== undefined ? { name: draft.name } : {}),
    ...(draft.notes !== undefined ? { notes: draft.notes } : {}),
    ...(draft.location !== undefined ? { location: draft.location } : {}),
    ...(draft.noteEvents !== undefined ? { noteEvents: draft.noteEvents } : {}),
    exercises,
    timeline,
    // Phase 1 always null. NOTE for Phase 2: this round-trip (LoggerDraft has
    // no activeExerciseId) resets it every dispatch — a Phase-2 action that
    // sets the active exercise must carry it outside this nested round-trip.
    activeExerciseId: null,
  };
};

// Flat → nested, STRIPPING every identity field. Re-groups the timeline by
// entryId (preserving each entry's set order) and rebuilds the entry list in
// `exercises` roster order. Stripping {id, entryId, exerciseId} off each set
// yields exactly today's DraftSet.
//
// ⚠️ Since LG-39 this has NO runtime caller: the live logger reads
// assembleDraftKeepIds (loggerDraft.ts), because the UI needs the row
// identity this function throws away. It stays because it IS the oracle
// contract — the definition of "what the old nested world sees", asserted in
// assembleDraft.test.ts and loggerTimelineReducer.test.ts against plain
// fixtures that carry no ids. ⛔ Do not delete it as dead code: deleting it
// deletes the proof that the flat store round-trips exactly.
export const assembleDraft = (state: WorkoutState): LoggerDraft => {
  const setsByEntry = new Map<string, DraftSet[]>();
  for (const set of state.timeline) {
    const { id: _id, entryId, exerciseId: _exerciseId, ...draftSet } = set;
    const list = setsByEntry.get(entryId);
    if (list) {
      list.push(draftSet);
    } else {
      setsByEntry.set(entryId, [draftSet]);
    }
  }
  const entries: DraftEntry[] = state.exercises.map((meta) => {
    const { entryId, ...entryMeta } = meta;
    return { ...entryMeta, sets: setsByEntry.get(entryId) ?? [] };
  });
  return {
    ...(state.sessionId !== undefined ? { sessionId: state.sessionId } : {}),
    startedAt: state.startedAt,
    ...(state.endedAt !== undefined ? { endedAt: state.endedAt } : {}),
    ...(state.fromPlanId !== undefined ? { fromPlanId: state.fromPlanId } : {}),
    ...(state.coachNotes !== undefined ? { coachNotes: state.coachNotes } : {}),
    ...(state.coachNotesRef !== undefined
      ? { coachNotesRef: state.coachNotesRef }
      : {}),
    ...(state.todayProposal !== undefined
      ? { todayProposal: state.todayProposal }
      : {}),
    ...(state.roundType !== undefined ? { roundType: state.roundType } : {}),
    ...(state.name !== undefined ? { name: state.name } : {}),
    ...(state.notes !== undefined ? { notes: state.notes } : {}),
    ...(state.location !== undefined ? { location: state.location } : {}),
    ...(state.noteEvents !== undefined ? { noteEvents: state.noteEvents } : {}),
    entries,
  };
};

// Like assembleDraft, but it KEEPS the identity fields on the nested view:
// each set keeps its `id` and each entry gets its `entryId` (as extra
// properties the existing nested reducer ignores but carries through its
// `{...set}` / `{...entry}` spreads). flattenDraft then reads those ids back,
// so a set logged once keeps the same id across reducer edits. This is the
// bridge that lets the flat store reuse the entire tested nested reducer
// instead of re-implementing ~30 actions against the flat shape. The result
// is a LoggerDraft for the reducer's eyes; the extra id fields are invisible
// to it.
export const assembleDraftKeepIds = (state: WorkoutState): LoggerDraft => {
  const setsByEntry = new Map<string, (DraftSet & { id: string })[]>();
  for (const set of state.timeline) {
    // Drop entryId + exerciseId (re-derived on flatten); keep id.
    const { entryId, exerciseId: _exerciseId, ...rest } = set;
    const list = setsByEntry.get(entryId);
    if (list) {
      list.push(rest);
    } else {
      setsByEntry.set(entryId, [rest]);
    }
  }
  const entries = state.exercises.map((meta) => {
    const { entryId, ...entryMeta } = meta;
    return { ...entryMeta, entryId, sets: setsByEntry.get(entryId) ?? [] };
  });
  return {
    ...(state.sessionId !== undefined ? { sessionId: state.sessionId } : {}),
    startedAt: state.startedAt,
    ...(state.endedAt !== undefined ? { endedAt: state.endedAt } : {}),
    ...(state.fromPlanId !== undefined ? { fromPlanId: state.fromPlanId } : {}),
    ...(state.coachNotes !== undefined ? { coachNotes: state.coachNotes } : {}),
    ...(state.coachNotesRef !== undefined
      ? { coachNotesRef: state.coachNotesRef }
      : {}),
    ...(state.todayProposal !== undefined
      ? { todayProposal: state.todayProposal }
      : {}),
    ...(state.roundType !== undefined ? { roundType: state.roundType } : {}),
    ...(state.name !== undefined ? { name: state.name } : {}),
    ...(state.notes !== undefined ? { notes: state.notes } : {}),
    // ⛔ Needed HERE above all: this is the projection flatReducer feeds the
    // reducer with. Missing here, `set_location` reads a draft that has no
    // place, so "correct the one I just set" never sees the previous value —
    // and missing from flattenDraft, whatever it writes is thrown away on the
    // way back. Both halves, or the field does not exist.
    ...(state.location !== undefined ? { location: state.location } : {}),
    ...(state.noteEvents !== undefined ? { noteEvents: state.noteEvents } : {}),
    entries,
  } as LoggerDraft;
};
